import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcessFlowchartNodeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';

import {ProcessFlowchartNodeAdapter} from '../flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import doesFlowchartHaveRouterWithAndOrFunction from '../flowchart-viz/util/doesFlowchartHaveRouterWithAndOrFunction';
import {
  ProcessFlowchartForValidation,
  ProcessFlowchartValidationError,
} from '../validation/ProcessFlowchartForValidationTypes';
import {ProcessFlowchartDesignSpace} from '../validation/ProcessGraphBuilderConstants';
import {pushErrors} from '../validation/validateProcessFlowchartHelpers';
import {
  ensureLoopsContainTaskAndRouterNode,
  ensureLoopsHaveOneEntryPoint,
} from '../validation/validateProcessFlowchartRules';
import {
  ensureChainedRouterLimit,
  ensureNoChainedRouters,
  ensureNoMultipleRouterOnlyPathsBetweenTaskNodes,
  ensureRouterNodeConnectedToNonEndNode,
  ensureRouterNodeDestinationNodesAreNotSamePTDV,
  ensureRouterNodeHasOneIncomingEdge,
  ensureRouterNodeHasOneSourceSchema,
  ensureRouterNodeHasRouteConfiguredForEachEdge,
  ensureRouterNodeHasTwoOutgoingEdges,
} from '../validation/validateProcessRouterNodes';

/**
 * Adapter allowing stop nodes to be rendered in the processes editor.
 */
export class StopNodeAdapter extends ProcessFlowchartNodeAdapter {
  nodeType: ProcessFlowchartNodeType = ProcessFlowchartNodeType.STOP_NODE;

  validateFlowchartForNodeType(
    flowchart: ProcessFlowchartForValidation,
    designSpace: ProcessFlowchartDesignSpace,
    didInitialFlowchartHaveChainedRouters: boolean,
    disconnectedNodeIds?: Array<string>
  ): Array<ProcessFlowchartValidationError> {
    let validationErrors: Array<ProcessFlowchartValidationError> = [];
    for (const node of flowchart.nodes) {
      if (node.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
        validationErrors = pushErrors(
          validationErrors,
          ensureRouterNodeConnectedToNonEndNode(node, flowchart)
        );
      }
    }
    return validationErrors;
  }
}

/**
 * Adapter allowing router nodes to be rendered in the processes editor.
 */
export class RouterNodeAdapter extends ProcessFlowchartNodeAdapter {
  nodeType: ProcessFlowchartNodeType = ProcessFlowchartNodeType.ROUTER_NODE;

  validateFlowchartForNodeType(
    flowchart: ProcessFlowchartForValidation,
    designSpace: ProcessFlowchartDesignSpace,
    didInitialFlowchartHaveChainedRouters: boolean,
    disconnectedNodeIds?: Array<string>
  ): Array<ProcessFlowchartValidationError> {
    let validationErrors: Array<ProcessFlowchartValidationError> = [];
    for (const node of flowchart.nodes) {
      if (node.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
        if (disconnectedNodeIds == null || !disconnectedNodeIds.includes(node.id)) {
          validationErrors = pushErrors(
            validationErrors,
            ensureRouterNodeHasOneIncomingEdge(node, flowchart.edges)
          );
          validationErrors = pushErrors(
            validationErrors,
            ensureRouterNodeHasTwoOutgoingEdges(node, flowchart.edges)
          );
          validationErrors = pushErrors(validationErrors, ensureChainedRouterLimit(node, flowchart));
        }
        if (
          !didInitialFlowchartHaveChainedRouters ||
          (didInitialFlowchartHaveChainedRouters && doesFlowchartHaveRouterWithAndOrFunction(flowchart))
        ) {
          validationErrors = pushErrors(validationErrors, ensureNoChainedRouters(node, flowchart));
        }
        validationErrors = pushErrors(
          validationErrors,
          ensureRouterNodeDestinationNodesAreNotSamePTDV({node, flowchart, usingFlowchartFirstConfig: true})
        );
        validationErrors = pushErrors(
          validationErrors,
          ensureRouterNodeHasRouteConfiguredForEachEdge(node, flowchart)
        );
        validationErrors = pushErrors(
          validationErrors,
          ensureRouterNodeHasOneSourceSchema({
            node,
            flowchart,
            designSpace,
            usingFlowchartFirstConfig: true,
          })
        );
      }
      if (node.nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS) {
        validationErrors = pushErrors(
          validationErrors,
          ensureNoMultipleRouterOnlyPathsBetweenTaskNodes({
            destinationNode: node,
            flowchart,
            usingFlowchartFirstConfig: true,
          })
        );
      }
      validationErrors = pushErrors(validationErrors, ensureLoopsContainTaskAndRouterNode(node, flowchart));
      validationErrors = pushErrors(validationErrors, ensureLoopsHaveOneEntryPoint(flowchart));
    }
    return validationErrors;
  }
}
